import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudQueue as CloudIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  rancherApi,
  RancherConfig,
  RancherCluster,
  RancherAuthRequest,
} from '../services/integrations-api';

export const RancherManager: React.FC = () => {
  const [instances, setInstances] = useState<RancherConfig[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<RancherConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authForm, setAuthForm] = useState<RancherAuthRequest>({
    instanceName: '',
    rancherUrl: '',
    username: '',
    password: '',
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<RancherConfig> & { password?: string }>({});

  useEffect(() => {
    loadInstances();
  }, []);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const data = await rancherApi.getAllInstances();
      setInstances(data);
      // Auto-select first instance if none selected
      if (data.length > 0 && !selectedInstance) {
        setSelectedInstance(data[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load Rancher instances');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setLoading(true);
      setError(null);

      await rancherApi.authenticate(authForm);

      // Reload instances
      await loadInstances();

      // Only close dialog and show success if everything worked
      setSuccess('Successfully authenticated with Rancher!');
      setAuthDialogOpen(false);
      setAuthForm({ instanceName: '', rancherUrl: '', username: '', password: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Authentication failed');
      // Don't close dialog on error so user can see the error message
    } finally {
      setLoading(false);
    }
  };

  const handleEditInstance = (instance: RancherConfig) => {
    setEditForm({
      name: instance.name,
      rancherUrl: instance.rancherUrl,
      username: instance.username,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedInstance) return;

    try {
      setLoading(true);
      setError(null);

      // If password is provided, re-authenticate first
      if (editForm.password) {
        await rancherApi.authenticate({
          instanceId: selectedInstance.id,
          instanceName: editForm.name || selectedInstance.name,
          rancherUrl: editForm.rancherUrl || selectedInstance.rancherUrl,
          username: editForm.username || selectedInstance.username,
          password: editForm.password,
        });
        setSuccess('Re-authenticated and updated Rancher instance successfully');
      } else {
        // Just update metadata
        await rancherApi.updateInstance(selectedInstance.id, editForm);
        setSuccess('Rancher instance updated successfully');
      }

      await loadInstances();
      setEditDialogOpen(false);
      setEditForm({});
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update instance');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInstance = async (instanceId: string) => {
    if (!confirm('Are you sure you want to delete this Rancher instance? All cluster configurations will be removed.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await rancherApi.deleteInstance(instanceId);

      // If we deleted the selected instance, clear selection
      if (selectedInstance?.id === instanceId) {
        setSelectedInstance(null);
      }

      await loadInstances();
      setSuccess('Rancher instance deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete instance');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setError(null);

      const result = await rancherApi.testConnection(
        authForm.rancherUrl || selectedInstance?.rancherUrl || '',
        undefined, // token - not available during initial setup
        authForm.username, // username for test authentication
        authForm.password  // password for test authentication
      );

      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleRefreshClusters = async () => {
    if (!selectedInstance) {
      setError('No Rancher instance selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const clusters = await rancherApi.refreshClusters();

      setSuccess(`Refreshed cluster list. Found ${clusters.length} clusters.`);
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to refresh clusters');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCluster = async (clusterId: string, enabled: boolean) => {
    try {
      setLoading(true);
      setError(null);

      await rancherApi.toggleCluster(clusterId, enabled);

      setSuccess(enabled ? 'Cluster enabled and kubeconfig generated' : 'Cluster disabled');
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to toggle cluster');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKubeconfig = async (clusterId: string) => {
    try {
      setLoading(true);
      setError(null);

      const kubeconfigPath = await rancherApi.generateKubeconfig(clusterId);

      setSuccess(`Kubeconfig generated at: ${kubeconfigPath}`);
      await loadInstances();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate kubeconfig');
    } finally {
      setLoading(false);
    }
  };

  if (loading && instances.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          <CloudIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Rancher Integration ({instances.length} instance{instances.length !== 1 ? 's' : ''})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAuthDialogOpen(true)}
          disabled={loading}
        >
          Add Rancher Instance
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Instance Selector */}
      {instances.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <FormControl fullWidth>
                  <InputLabel>Select Rancher Instance</InputLabel>
                  <Select
                    value={selectedInstance?.id || ''}
                    label="Select Rancher Instance"
                    onChange={(e) => {
                      const instance = instances.find(i => i.id === e.target.value);
                      setSelectedInstance(instance || null);
                    }}
                  >
                    {instances.map((instance) => (
                      <MenuItem key={instance.id} value={instance.id}>
                        <Box>
                          <Typography variant="body1">{instance.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {instance.rancherUrl}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Tooltip title="Refresh clusters from Rancher">
                    <IconButton
                      onClick={handleRefreshClusters}
                      disabled={loading || !selectedInstance}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit this Rancher instance">
                    <IconButton
                      onClick={() => selectedInstance && handleEditInstance(selectedInstance)}
                      disabled={loading || !selectedInstance}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete this Rancher instance">
                    <IconButton
                      onClick={() => selectedInstance && handleDeleteInstance(selectedInstance.id)}
                      disabled={loading || !selectedInstance}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Configuration Status */}
      {selectedInstance ? (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Rancher URL:</strong> {selectedInstance.rancherUrl}
            </Typography>
            {selectedInstance.lastAuthenticated && (
              <Typography variant="body2">
                <strong>Last Authenticated:</strong> {new Date(selectedInstance.lastAuthenticated).toLocaleString()}
              </Typography>
            )}
          </Alert>

          {/* Clusters */}
          <Typography variant="subtitle1" gutterBottom>
            Clusters ({selectedInstance.clusters.length})
          </Typography>

          {selectedInstance.clusters.length === 0 ? (
            <Alert severity="warning">
              No clusters found. Click the refresh button to fetch clusters from Rancher.
            </Alert>
          ) : (
            selectedInstance.clusters.map((cluster) => (
              <Accordion key={cluster.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>{cluster.name}</Typography>
                      {cluster.enabled && (
                        <Chip
                          label="Enabled"
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon />}
                        />
                      )}
                      {cluster.kubeconfigGenerated && (
                        <Chip
                          label="Kubeconfig Generated"
                          size="small"
                          color="primary"
                          icon={<LinkIcon />}
                        />
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Cluster ID:</strong> {cluster.id}
                      </Typography>
                      {cluster.kubeconfigPath && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Kubeconfig Path:</strong> {cluster.kubeconfigPath}
                        </Typography>
                      )}
                      {cluster.lastUpdated && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Last Updated:</strong> {new Date(cluster.lastUpdated).toLocaleString()}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" gap={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={cluster.enabled}
                              onChange={(e) => handleToggleCluster(cluster.id, e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label={cluster.enabled ? 'Enabled' : 'Disabled'}
                        />
                        {!cluster.kubeconfigGenerated && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateKubeconfig(cluster.id)}
                            disabled={loading}
                          >
                            Generate Kubeconfig
                          </Button>
                        )}
                        {cluster.kubeconfigGenerated && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateKubeconfig(cluster.id)}
                            disabled={loading}
                          >
                            Regenerate Kubeconfig
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      ) : (
        <Alert severity="info">
          No Rancher instances configured. Click "Add Rancher Instance" to get started.
        </Alert>
      )}

      {/* Authentication Dialog */}
      <Dialog
        open={authDialogOpen}
        onClose={() => !loading && setAuthDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Connect to Rancher</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instance Name"
                placeholder="e.g., Production Rancher"
                value={authForm.instanceName}
                onChange={(e) => setAuthForm({ ...authForm, instanceName: e.target.value })}
                helperText="A friendly name to identify this Rancher instance"
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rancher URL"
                placeholder="https://rancher.example.com"
                value={authForm.rancherUrl}
                onChange={(e) => setAuthForm({ ...authForm, rancherUrl: e.target.value })}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={authForm.username}
                onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleTestConnection}
                disabled={loading || testingConnection || !authForm.rancherUrl}
                startIcon={testingConnection ? <CircularProgress size={16} /> : undefined}
              >
                Test Connection
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAuthenticate}
            variant="contained"
            disabled={
              loading ||
              !authForm.instanceName ||
              !authForm.rancherUrl ||
              !authForm.username ||
              !authForm.password
            }
          >
            {loading ? <CircularProgress size={24} /> : 'Authenticate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Instance Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Rancher Instance</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instance Name"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rancher URL"
                  value={editForm.rancherUrl || ''}
                  onChange={(e) => setEditForm({ ...editForm, rancherUrl: e.target.value })}
                  disabled={loading}
                  helperText="e.g., https://rancher.example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  value={editForm.username || ''}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  disabled={loading}
                  helperText="Leave empty to keep current username"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password (optional)"
                  value={editForm.password || ''}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  disabled={loading}
                  helperText="Enter password to re-authenticate and refresh token"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={loading || !editForm.name}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
